/** Scroll-driven timeline used by article shortcodes. */

type TimelineParts = {
  root: HTMLElement
  steps: HTMLElement[]
  nodes: HTMLElement[]
  centerYear: HTMLElement | null
  centerTitle: HTMLElement | null
}

const LOOP_CIRCUMFERENCE = 855

function setActive(parts: TimelineParts, index: number): void {
  const clamped = Math.max(0, Math.min(index, parts.steps.length - 1))
  const progress = parts.steps.length <= 1 ? 1 : clamped / (parts.steps.length - 1)

  parts.root.style.setProperty('--loop-offset', String(LOOP_CIRCUMFERENCE * (1 - progress)))
  parts.root.dataset.active = String(clamped)

  parts.steps.forEach((step, stepIndex) => {
    step.classList.toggle('is-active', stepIndex === clamped)
  })

  parts.nodes.forEach((node, nodeIndex) => {
    node.classList.toggle('is-active', nodeIndex === clamped)
  })

  const activeStep = parts.steps[clamped]
  const year = activeStep?.querySelector<HTMLElement>('.loop-timeline__year')?.textContent || ''
  const title = activeStep?.querySelector<HTMLElement>('h4')?.textContent || ''

  if (parts.centerYear) parts.centerYear.textContent = year
  if (parts.centerTitle) parts.centerTitle.textContent = title
}

export function initLoopTimeline(): void {
  const timelines = Array.from(document.querySelectorAll<HTMLElement>('[data-loop-timeline]'))
  if (timelines.length === 0) return

  timelines.forEach((root) => {
    const parts: TimelineParts = {
      root,
      steps: Array.from(root.querySelectorAll<HTMLElement>('.loop-timeline__step')),
      nodes: Array.from(root.querySelectorAll<HTMLElement>('.loop-timeline__node')),
      centerYear: root.querySelector<HTMLElement>('.loop-timeline__center-year'),
      centerTitle: root.querySelector<HTMLElement>('.loop-timeline__center strong'),
    }

    if (parts.steps.length === 0) return

    setActive(parts, 0)

    if (!('IntersectionObserver' in window)) return

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0]

        if (!visible) return

        const index = Number((visible.target as HTMLElement).dataset.stepIndex || '0')
        setActive(parts, index)
      },
      {
        root: null,
        threshold: [0.32, 0.48, 0.64],
        rootMargin: '-18% 0px -30% 0px',
      },
    )

    parts.steps.forEach((step) => observer.observe(step))
  })
}
